import { useEffect, useMemo, useRef, useState } from 'react'
import { skillService, type SkillCatalogResult } from '../../services/skills'
import { useI18n } from '../../i18n/I18nContext'

type SkillPickerProps = {
  selectedSkills: SkillCatalogResult[]
  onChange: (skills: SkillCatalogResult[]) => void
  label?: string
  placeholder?: string
  requestContext?: string
  disabled?: boolean
}

export function SkillPicker({
  selectedSkills,
  onChange,
  label = 'Search skills',
  placeholder = 'Start typing a skill',
  requestContext,
  disabled = false,
}: SkillPickerProps) {
  const { t } = useI18n()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SkillCatalogResult[]>([])
  const [canRequestNewSkill, setCanRequestNewSkill] = useState(false)
  const [loading, setLoading] = useState(false)
  const [requesting, setRequesting] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const latestQuery = useRef('')
  const selectedIds = useMemo(() => new Set(selectedSkills.map((skill) => skill.id)), [selectedSkills])

  useEffect(() => {
    const trimmed = query.trim()
    latestQuery.current = trimmed
    setMessage(null)
    if (!trimmed) {
      setResults([])
      setCanRequestNewSkill(false)
      return
    }
    const handle = window.setTimeout(() => {
      setLoading(true)
      skillService.searchSkills(trimmed)
        .then((data) => {
          if (latestQuery.current !== trimmed) return
          setResults(data.results || [])
          setCanRequestNewSkill(Boolean(data.canRequestNewSkill))
        })
        .catch(() => {
          if (latestQuery.current !== trimmed) return
          setResults([])
          setCanRequestNewSkill(false)
          setMessage(t('Skill search is unavailable.'))
        })
        .finally(() => {
          if (latestQuery.current === trimmed) setLoading(false)
        })
    }, 250)
    return () => window.clearTimeout(handle)
  }, [query, t])

  const addSkill = (skill: SkillCatalogResult) => {
    if (selectedIds.has(skill.id)) return
    onChange([...selectedSkills, skill])
    setQuery('')
    setResults([])
    setCanRequestNewSkill(false)
  }

  const removeSkill = (skillId: string) => {
    onChange(selectedSkills.filter((skill) => skill.id !== skillId))
  }

  const requestSkill = () => {
    const requestedName = query.trim()
    if (!requestedName) return
    setRequesting(true)
    skillService.requestNewSkill(requestedName, requestContext)
      .then(() => {
        setMessage(t('Skill request sent for review.'))
        setCanRequestNewSkill(false)
      })
      .catch(() => setMessage(t('Could not send the skill request.')))
      .finally(() => setRequesting(false))
  }

  return (
    <div className="skill-picker">
      <label className="field-label">
        {label}
        <input
          className="input"
          value={query}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(event) => setQuery(event.target.value)}
        />
      </label>

      {selectedSkills.length > 0 ? (
        <div className="admin-chip-list skill-picker-selected">
          {selectedSkills.map((skill) => (
            <span className="admin-chip skill-chip" key={skill.id}>
              {skill.canonicalName}
              <button type="button" className="chip-remove" aria-label={t('Remove {name}', { name: skill.canonicalName })} onClick={() => removeSkill(skill.id)}>x</button>
            </span>
          ))}
        </div>
      ) : null}

      {query.trim() ? (
        <div className="skill-picker-results" role="listbox">
          {loading ? <div className="skill-picker-row muted">{t('Searching...')}</div> : null}
          {!loading && results.map((skill) => {
            const duplicate = selectedIds.has(skill.id)
            return (
              <button
                type="button"
                className="skill-picker-row"
                key={skill.id}
                disabled={duplicate}
                onClick={() => addSkill(skill)}
              >
                <span>
                  <strong>{skill.canonicalName}</strong>
                  {skill.matchType === 'fuzzy' ? <small>{t('Did you mean?')}</small> : null}
                </span>
                <span className="skill-picker-tags">
                  {skill.category?.name ? <span>{skill.category.name}</span> : null}
                  {(skill.industries || []).slice(0, 3).map((industry) => <span key={industry.id}>{industry.name}</span>)}
                  {duplicate ? <span>{t('Selected')}</span> : null}
                </span>
              </button>
            )
          })}
          {!loading && results.length === 0 ? <div className="skill-picker-row muted">{t('No matching catalog skill.')}</div> : null}
          {canRequestNewSkill ? (
            <button type="button" className="skill-picker-request" disabled={requesting} onClick={requestSkill}>
              {requesting ? t('Requesting...') : t('Request "{name}"', { name: query.trim() })}
            </button>
          ) : null}
        </div>
      ) : null}

      {message ? <p className="helper">{message}</p> : null}
    </div>
  )
}
